import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import Spinner from '../components/Spinner';
import HelpSubnav from '../components/help/HelpSubnav';
import { getHelpAreaLabel, getHelpArticlePath, getHelpSectionLabel, getHelpSectionPath } from '../components/help/helpConstants';
import { GET_HELP_ARTICLE } from '../graphql/helpContent';

const LISTING_START = '<!-- listing:start -->';
const LISTING_END = '<!-- listing:end -->';

const getCopyableListing = (markdown) => {
  if (!markdown) return '';
  const start = markdown.indexOf(LISTING_START);
  const end = markdown.indexOf(LISTING_END);
  if (start === -1 || end === -1 || end <= start) return markdown.trim();
  return markdown.slice(start + LISTING_START.length, end).trim();
};

export default function HelpArticle() {
  const { slug } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { data, loading, error } = useQuery(GET_HELP_ARTICLE, {
    variables: { slug },
    skip: !slug,
  });
  const article = data?.helpArticle;
  const [copyLabel, setCopyLabel] = useState('Copy listing');
  const listingText = getCopyableListing(article?.bodyMarkdown);
  const canCopyListing = Boolean(listingText) && article?.bodyMarkdown?.includes(LISTING_START);

  const handleCopyListing = async () => {
    if (!listingText) return;
    try {
      await navigator.clipboard.writeText(listingText);
      setCopyLabel('Copied');
      window.setTimeout(() => setCopyLabel('Copy listing'), 2000);
    } catch {
      setCopyLabel('Copy failed');
      window.setTimeout(() => setCopyLabel('Copy listing'), 2000);
    }
  };

  useEffect(() => {
    setCopyLabel('Copy listing');
  }, [slug]);

  useEffect(() => {
    if (!article) return;
    const expectedPath = getHelpArticlePath(article);
    if (location.pathname !== expectedPath) {
      navigate(expectedPath, { replace: true });
    }
  }, [article, location.pathname, navigate]);

  const backPath = getHelpSectionPath(article?.kind);

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <Link
          to={backPath}
          className="inline-flex items-center text-sm font-medium text-sky-100 hover:text-white lg:text-sky-800 lg:hover:text-sky-900"
        >
          ← Back to {getHelpSectionLabel(article?.kind)}
        </Link>
        <HelpSubnav />
      </div>

      {loading ? (
        <div className="flex justify-center py-16">
          <Spinner />
        </div>
      ) : error || !article ? (
        <div className="rounded-xl border border-red-200 bg-red-50 p-6 text-sm text-red-700">
          This article was not found or is not available for your role.
        </div>
      ) : (
        <article className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
          <header className="border-b border-gray-200 px-6 py-5">
            <p className="text-xs font-semibold uppercase tracking-wide text-sky-700">
              {getHelpAreaLabel(article.area)}
            </p>
            <h1 className="mt-1 text-2xl font-semibold text-gray-900">{article.title}</h1>
            {article.summary ? (
              <p className="mt-2 text-sm text-gray-600">{article.summary}</p>
            ) : null}
            {canCopyListing ? (
              <button
                type="button"
                onClick={handleCopyListing}
                className="mt-4 inline-flex items-center rounded-lg bg-sky-700 px-4 py-2 text-sm font-medium text-white hover:bg-sky-800"
                aria-label="Copy job listing text"
              >
                {copyLabel}
              </button>
            ) : null}
          </header>
          <div
            className="prose max-w-none px-6 py-6"
            dangerouslySetInnerHTML={{ __html: article.bodyHtml }}
          />
        </article>
      )}
    </div>
  );
}
